import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, BarChart3, Mail, ShieldCheck } from "lucide-react";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { Logo } from "@/components/rentalmnp/shared/Logo";

const TITLE = "Politique de confidentialité | RentaLMNP";
const DESC =
  "Comment RentaLMNP traite vos données : analytics anonymes, pas de cookies de tracking, simulations stockées chez vous.";

export const Route = createFileRoute("/confidentialite")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://rentalmnp.com/confidentialite" },
    ],
    links: [{ rel: "canonical", href: "https://rentalmnp.com/confidentialite" }],
  }),
  component: ConfidentialitePage,
});

function ConfidentialitePage() {
  const today = new Date().toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-3xl px-6 py-8 lg:px-12">
        <div className="mb-6 flex items-center justify-between">
          <Logo size={36} />
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition hover:text-primary"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
        </div>

        <header className="mt-6 mb-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-success/30 bg-success/10 px-3 py-1 text-xs font-medium text-success">
            <ShieldCheck className="h-3.5 w-3.5" />
            100% conforme RGPD — sans cookie de tracking
          </div>
          <h1 className="mt-4 text-3xl font-extrabold text-foreground sm:text-4xl">
            Politique de confidentialité
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">Dernière mise à jour : {today}</p>
        </header>

        <div className="prose prose-sm max-w-none space-y-8 text-foreground/90">
          <section>
            <h2 className="text-xl font-bold text-foreground">Qui nous sommes</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              RentaLMNP est un simulateur de rentabilité LMNP édité depuis la France.
              Pour toute question liée à vos données, écrivez-nous à{" "}
              <a
                href="mailto:contact@rentalmnp.fr"
                className="font-medium text-primary hover:underline"
              >
                contact@rentalmnp.fr
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Quelles données nous collectons</h2>

            <h3 className="mt-4 flex items-center gap-2 text-base font-semibold text-foreground"><BarChart3 className="h-4 w-4 text-primary" aria-hidden />Sur le site (anonyme)</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Nous utilisons <strong className="text-foreground">Plausible Analytics</strong>, un
              outil de mesure d'audience européen (UE) qui{" "}
              <strong className="text-foreground">ne dépose aucun cookie</strong> et{" "}
              <strong className="text-foreground">ne stocke aucune adresse IP</strong>. Nous savons
              combien de visiteurs utilisent le simulateur et quelles pages convertissent —
              jamais qui vous êtes.
            </p>

            <h3 className="mt-4 flex items-center gap-2 text-base font-semibold text-foreground"><Mail className="h-4 w-4 text-primary" aria-hidden />Si vous nous donnez votre email</h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">Nous stockons uniquement :</p>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-muted-foreground">
              <li>Votre adresse email</li>
              <li>Votre prénom (si vous l'avez fourni)</li>
              <li>La date d'inscription</li>
            </ul>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              <strong className="text-foreground">Nous ne stockons jamais</strong> les détails de
              vos simulations sur nos serveurs. Vos simulations restent uniquement sur votre
              appareil (LocalStorage de votre navigateur).
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">À quoi servent vos données</h2>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-muted-foreground">
              <li>Vous envoyer l'analyse de votre simulation</li>
              <li>Vous envoyer la newsletter hebdomadaire LMNP (si vous l'avez accepté)</li>
              <li><strong className="text-foreground">Rien d'autre.</strong></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Avec qui nous partageons</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              <strong className="text-foreground">Personne.</strong> Vos données ne sont jamais
              vendues ni partagées avec des tiers.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Lorsque vous cliquez sur un{" "}
              <Link to="/partenaires" className="font-medium text-primary hover:underline">
                lien partenaire
              </Link>{" "}
              (ex : Meilleurtaux), vous êtes redirigé chez eux. À partir de là, leur propre
              politique de confidentialité s'applique.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Combien de temps nous gardons vos données</h2>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-muted-foreground">
              <li>Emails newsletter : tant que vous êtes abonné</li>
              <li>Après désabonnement : supprimé sous 30 jours</li>
              <li>Statistiques Plausible : 24 mois glissants (anonymisées)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Vos droits (RGPD)</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Vous pouvez à tout moment :
            </p>
            <ul className="mt-2 list-disc space-y-1 pl-6 text-sm text-muted-foreground">
              <li>Vous désabonner via le lien présent dans chaque email</li>
              <li>
                Demander la suppression ou l'accès à vos données :{" "}
                <a
                  href="mailto:contact@rentalmnp.fr"
                  className="font-medium text-primary hover:underline"
                >
                  contact@rentalmnp.fr
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Cookies</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Le simulateur n'utilise{" "}
              <strong className="text-foreground">aucun cookie de tracking</strong>. Seul le
              LocalStorage de votre navigateur est utilisé pour sauvegarder vos simulations,
              et uniquement chez vous.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-foreground">Réclamations</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Vous pouvez à tout moment saisir la CNIL :{" "}
              <a
                href="https://www.cnil.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary hover:underline"
              >
                www.cnil.fr
              </a>
              .
            </p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
